import { useAtom } from "jotai";
import {
    groupedProviderLocationsAtom,
    providerLocationsAtom,
    providerLocationsFilterAtom
} from "./analysisAtoms";
import { useQuery } from "@tanstack/react-query";
import { mbxUserAtom } from "../../atoms/atoms";
import { execReadBySortkey } from "../../modules/DBService";
import _ from "lodash";
import { circle } from "@turf/circle";
import { graphql, graphqlOperation } from "../../modules/AmplifyServices";

export const getProviderLocation = /* GraphQL */ `
    query GetProviderLocation($id: ID!) {
        getProviderLocation(id: $id) {
            id
            providerId
            name
            desc
            addressoneline
            street
            city
            state
            country
            postalcode
            longitude
            latitude
            timezone
            maxtraveltype
            traveldistance
            traveldistanceunit
            travelregions
            updatedAt
        }
    }
`;

export const providerByCompany = /* GraphQL */ `
    query ProviderByCompany(
        $companyId: String
        $firstnameLastnameCreatedAt: ModelProviderByCompanyCompositeKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelProviderFilterInput
        $limit: Int
        $nextToken: String
    ) {
        providerByCompany(
            companyId: $companyId
            firstnameLastnameCreatedAt: $firstnameLastnameCreatedAt
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                firstname
                lastname
                emailaddress
                phone
                longitude
                latitude
                timezone
                pictures3key
                active
                deleted
                ratingstarsavg
                maxTravelTime
                createdAt
                updatedAt
            }
            nextToken
        }
    }
`;
const providerScheduleByCompany = /* GraphQL */ `
    query ProviderScheduleByCompany(
        $companyId: String
        $scheduleinfo: ModelStringKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelProviderScheduleFilterInput
        $limit: Int
        $nextToken: String
    ) {
        providerScheduleByCompany(
            companyId: $companyId
            scheduleinfo: $scheduleinfo
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                scheduleinfo
                providerId
                locations
                services
                active
                startDate
                sdtutc
                tz
                endDate
                status
                deleted
                rrule
                startTime
                endTime
                weekDays
                updatedAt
            }
            nextToken
        }
    }
`;
function preparePolygon(providerLocation) {
    if (
        providerLocation.maxtraveltype === "DRAW_TRAVEL_REGION" &&
        !(providerLocation.travelregions instanceof Array)
    ) {
        try {
            providerLocation.travelregions = JSON.parse(
                providerLocation.travelregions
            );
        } catch (e) {
            console.log(
                "error parsing travelregion for providerlocation",
                providerLocation
            );
        }
    } else if (providerLocation.maxtraveltype === "MAX_DISTANCE") {
        providerLocation.travelregions = [createCircle(providerLocation)];
    }
    return providerLocation;
}
function createCircle(pl) {
    const center = [pl.longitude, pl.latitude];
    let traveldistance = 0;
    if (pl.traveldistanceunit === "km") traveldistance = pl.traveldistance;
    //if no km, assume miles
    else traveldistance = pl.traveldistance * 1.60934;

    const options = { steps: 32, units: "kilometers" };
    const polygonedCircle = circle(center, traveldistance, options);
    return polygonedCircle.geometry.coordinates[0]
        .slice(0, polygonedCircle.geometry.coordinates[0].length - 1)
        .map((coordinate) => {
            return { lng: coordinate[0], lat: coordinate[1] };
        });
}
export function useProviderSchedule() {
    const [mbxUser] = useAtom(mbxUserAtom);
    const [, setProviderLocations] = useAtom(providerLocationsAtom);
    const [, setProviderLocationsFilter] = useAtom(providerLocationsFilterAtom);
    const [, setGroupedProviderLocations] = useAtom(
        groupedProviderLocationsAtom
    );
    return useQuery({
        queryKey: ["providers-and-zones"],
        queryFn: async () => {
            try {
                let providersQuery = {
                    opname: "providerByCompany",
                    op: providerByCompany,
                    id: {
                        companyId: mbxUser.companyId
                    },
                    limit: 200,
                    sortDirection: "ASC",
                    filter: {
                        and: [
                            { active: { ne: false } },
                            { deleted: { ne: true } }
                        ]
                    }
                };
                const { items: providers } =
                    await execReadBySortkey(providersQuery);
                const { items: providerScheduleRecords } =
                    await execReadBySortkey({
                        opname: "providerScheduleByCompany",
                        op: providerScheduleByCompany,
                        id: {
                            companyId: mbxUser.companyId
                        },
                        skey: {
                            scheduleinfo: { beginsWith: "SB" }
                        },
                        filter: { deleted: { ne: true }, active: { eq: true } },
                        limit: 500
                    });
                const schedulesGroupedByProvider = _.groupBy(
                    providerScheduleRecords,
                    "providerId"
                );
                let providerLocationIds = new Set();
                for (let provider of providers) {
                    provider.schedules = [];
                    const schedulesOfProvider =
                        schedulesGroupedByProvider[provider.id];
                    if (schedulesOfProvider)
                        for (let schedule of schedulesOfProvider) {
                            try {
                                if (schedule.locations) {
                                    const providerLocationId = JSON.parse(
                                        schedule.locations
                                    )
                                        .find(
                                            (location) =>
                                                location.indexOf("PL") !== -1
                                        )
                                        ?.substring(3);
                                    if (providerLocationId) {
                                        providerLocationIds.add(
                                            providerLocationId
                                        );
                                        schedule.providerLocationId =
                                            providerLocationId;
                                        provider.schedules.push(schedule); // only have schedules with travel zones
                                    }
                                }
                            } catch (e) {
                                console.log(
                                    "error while reading schedule of provider for analysis",
                                    schedule
                                );
                            }
                        }
                }
                const providerLocationChunks = _.chunk(
                    Array.from(providerLocationIds),
                    10
                );
                const cleanProviderLocations = [];
                const groupKeys = new Set();
                for (const chunk of providerLocationChunks) {
                    const results = chunk.map((provLocId) =>
                        graphql(
                            graphqlOperation(getProviderLocation, {
                                id: provLocId
                            })
                        )
                    );

                    const locations = await Promise.all(results);
                    locations
                        .filter(
                            //filter out bad data travel zones (they cannot be shown on the map)
                            (result) =>
                                (result &&
                                    result.data.getProviderLocation &&
                                    result.data.getProviderLocation
                                        .maxtraveltype ===
                                        "DRAW_TRAVEL_REGION" &&
                                    (result.data.getProviderLocation
                                        .travelregions ||
                                        result.data.getProviderLocation
                                            .travelregions.length > 10)) ||
                                result.data.getProviderLocation
                                    .maxtraveltype === "MAX_DISTANCE"
                        )
                        .forEach((result) => {
                            if (result && result.data.getProviderLocation) {
                                const provider = providers.find(
                                    (provider) =>
                                        provider.id ===
                                        result.data.getProviderLocation
                                            .providerId
                                );

                                if (provider.schedules)
                                    result.data.getProviderLocation.SBs =
                                        provider.schedules.filter(
                                            (sch) =>
                                                sch.providerLocationId ===
                                                result.data.getProviderLocation
                                                    .id
                                        );
                                const sortKey = `${result.data.getProviderLocation.country},${result.data.getProviderLocation.state}`;
                                groupKeys.add(sortKey);
                                cleanProviderLocations.push({
                                    ...preparePolygon(
                                        result.data.getProviderLocation
                                    ),
                                    firstname: provider.firstname,
                                    lastname: provider.lastname,
                                    phone: provider.phone,
                                    emailaddress: provider.emailaddress,
                                    pictures3key: provider.pictures3key,
                                    ratingstarsavg: provider.ratingstarsavg,
                                    maxTravelTime: provider.maxTravelTime,
                                    sortKey,
                                    groupDisplayName: `${result.data.getProviderLocation.state}, ${result.data.getProviderLocation.country}`
                                });
                            }
                        });
                }
                setProviderLocations(cleanProviderLocations);
                const sortedGroupKeys = Array.from(groupKeys).sort(
                    (key1, key2) => {
                        if (key1 && key2) {
                            return key1.localeCompare(key2);
                        } else if (key1) {
                            return 1;
                        } else if (key2) {
                            return -1;
                        } else return 0;
                    }
                );
                const groupedProviderLocations = {};
                const initialFilteredProviderLocationIds = [];
                let runningCountOfProviders = 0;
                sortedGroupKeys.forEach((key, index) => {
                    const keyMatchingProviderLocations =
                        cleanProviderLocations.filter(
                            (pl) => pl.sortKey === key
                        );

                    keyMatchingProviderLocations.forEach((pl) => {
                        const providerName = `${pl.firstname} ${pl.lastname}`;
                        const providerEmail = pl.emailaddress;
                        const groupName = pl.groupDisplayName;
                        if (!groupedProviderLocations[groupName])
                            groupedProviderLocations[groupName] = {};

                        if (
                            !groupedProviderLocations[groupName][providerName]
                        ) {
                            groupedProviderLocations[groupName][providerName] =
                                {
                                    ids: [pl.id],
                                    emailaddress: providerEmail
                                };
                            runningCountOfProviders++;
                            if (runningCountOfProviders <= 20) {
                                initialFilteredProviderLocationIds.push(pl.id);
                            }
                        } else {
                            groupedProviderLocations[groupName][
                                providerName
                            ].ids.push(pl.id);
                            if (runningCountOfProviders <= 20) {
                                initialFilteredProviderLocationIds.push(pl.id);
                            }
                        }
                    });
                });
                setGroupedProviderLocations(groupedProviderLocations);
                setProviderLocationsFilter(initialFilteredProviderLocationIds);

                return providers;
            } catch (e) {
                console.log("error bookings-bks", e);
            }
        }
    });
}
